// REACT
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

// YARN
import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import { ThemeProvider } from '@mui/material/styles';
import Button from '@mui/material/Button';

// SERVICES
import dropletLogo from 'assets/images/laissez-tomber.png';
import './CulturesMapTab.style.scss';
import { getMainCultureByYear } from './MapCommon';

// INTERFACES
import { IParcelCulture } from '../Interface/Api/Response/Exploitation/Parcels/IParcelCulture';
import { IResultInfo } from '../Interface/Simulation/IResultInfo';
import ClearIcon from '@mui/icons-material/Clear';
import { IResultCultures } from '../Interface/Simulation/IResultCultures';

// THEMES
import { customButtonTheme } from 'assets/styles/themes/generic_button_mui';
import { customDialogAddEditCommonTheme } from 'assets/styles/themes/Dialog/generic_dialog_add_edit_parcel';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import { ORGANIC_STATES } from 'utils/GlobalVariables';
import { getParcelOrganicStateAtYear } from 'utils/tools_functions';
import {
  getFilteredCultures,
  getSeasonFilter,
} from 'components/pages/Exploitation/Simulation/Result/utils/utilsResult';

const ResultsInfo = ({ modal, setModal, resultsInfo, cultures, year, updateFromMap }) => {
  const { t } = useTranslation();
  const checkCultureByYear = () => {
    return resultsInfo.cultures.find((culture) => culture.year === year) || null;
  };

  const [currentCulture, setCurrentCulture] = useState(checkCultureByYear());

  const handleCultureChange = (event, index) => {
    const { value } = event.target;
    const cultureId = Number.parseInt(value);
    const selectedCulture = cultures.find((culture) => culture.id === cultureId);
    const currentCultureClone = structuredClone(currentCulture);

    currentCultureClone[index] = {
      ...currentCultureClone[index],
      id: cultureId,
      name: selectedCulture?.name,
      type: selectedCulture?.type || currentCultureClone[index].type,
      customizedFor: selectedCulture?.customizedFor || currentCultureClone[index].customizedFor,
      season: selectedCulture?.season || currentCultureClone[index].season,
    };

    setCurrentCulture(currentCultureClone);
  };

  const handleUpdate = (parcelId, updatedCultures, year) => {
    console.log('handleUpdate:', { parcelId, updatedCultures, year });
    updateFromMap(parcelId, updatedCultures, year);
  };

  const currentOrganicState =
    getParcelOrganicStateAtYear(resultsInfo, year) || ORGANIC_STATES.conventional;

  const culturesWithIndex =
    currentCulture?.map((culture, idx) => ({
      culture,
      index: idx,
      isSplit: !!culture.isSplit,
    })) || [];

  const hasSplit = culturesWithIndex.some((c) => c.isSplit);

  return (
    <ThemeProvider theme={customDialogAddEditCommonTheme}>
      <Dialog
        maxWidth="md"
        fullWidth
        open={modal}
        onClose={() => setModal(false)}
        id="results-modal"
      >
        <Box className="modal_header">
          <DialogTitle>
            {resultsInfo?.name}{' '}
            {hasSplit && (
              <div>
                <ContentCutIcon />
              </div>
            )}
          </DialogTitle>
          <ClearIcon className="close-icon" onClick={() => setModal(false)} />
        </Box>
        <DialogContent>
          <Box>
            <InputLabel id="surface">{t('common.surface')} :</InputLabel>
            <TextField id="surface" value={resultsInfo?.surface} disabled={true} />
          </Box>
          {resultsInfo?.groupSurface && (
            <Box>
              <InputLabel id="groupSurface">{t('label.groupSurface')} :</InputLabel>
              <TextField
                id="groupSurface"
                value={Number(resultsInfo?.groupSurface.toFixed(2))}
                disabled={true}
              />
            </Box>
          )}
          <Box>
            <InputLabel id="previousCulture1">{t('common.previous')} :</InputLabel>
            <TextField
              id="previousCulture1"
              value={getMainCultureByYear(year - 1, resultsInfo, t)}
              disabled={true}
            />
          </Box>
          <Box>
            <InputLabel id="previousCulture2">{t('label.antePrevious')} :</InputLabel>
            <TextField
              id="previousCulture2"
              value={getMainCultureByYear(year - 2, resultsInfo, t)}
              disabled={true}
            />
          </Box>
          <Box>
            <InputLabel id="groundType">{t('label.groundType')} :</InputLabel>
            <Select id="groundType" value={resultsInfo?.groundType?.name} disabled={true}>
              <MenuItem key={resultsInfo?.groundType?.id} value={resultsInfo?.groundType?.name}>
                <ListItemText primary={resultsInfo?.groundType?.name} />
              </MenuItem>
            </Select>
          </Box>
          <Box
            id="irrigable-center"
            className={`${
              hasSplit || resultsInfo?.groupSurface ? 'irrigable-center-splitted' : ''
            }`}
          >
            <Box>
              <InputLabel id="irrigable">{t('label.irrigable')} :</InputLabel>
              <Checkbox
                disabled={true}
                className="checkbox-irrigable"
                id="irrigable"
                checked={resultsInfo?.isIrrigableOnly}
              />
            </Box>
          </Box>
          <Stack
            spacing={1}
            direction="row"
            useFlexGap
            flexWrap="wrap"
            className="result-info-stack result-info-stack-organicState"
          >
            <InputLabel>{t('label.parcelState')} :</InputLabel>
            <Chip
              label={`${
                currentOrganicState === ORGANIC_STATES.conversion
                  ? t('organicState.conversion')
                  : currentOrganicState === 'organic'
                    ? t('organicState.organic')
                    : t('organicState.conventional')
              }`}
              variant="outlined"
              className="result-info-chip"
            />
          </Stack>
          {resultsInfo?.groupParcelNames && (
            <Stack
              spacing={1}
              direction="row"
              useFlexGap
              flexWrap="wrap"
              className="result-info-stack"
            >
              <span>{t('label.groupedWith')} :</span>
              {resultsInfo?.groupParcelNames.map((parcelName, index) => (
                <Chip
                  key={index}
                  label={parcelName}
                  variant="outlined"
                  className="result-info-chip"
                />
              ))}
            </Stack>
          )}
          <DialogContent dividers>
            <Box className="wrapper-cultures-select">
              <InputLabel className="wrapper-cultures-select__year">
                {year} - {year + 1}
              </InputLabel>
              <React.Fragment>
                {hasSplit ? (
                  <>
                    {culturesWithIndex.map(({ culture, index }, position) => {
                      const culturesList = getFilteredCultures(
                        'single',
                        cultures,
                        currentOrganicState,
                      );

                      return (
                        <Box className="wrapper-cultures-select__splitted" key={index}>
                          <Box>
                            <div className="wrapper-cultures-select__infos">
                              <h4 className="wrapper-cultures-select__title">
                                {t('common.parcel')} {position + 1}
                              </h4>
                              <div>
                                <span>{t('common.surface')} : </span>
                                <span className="wrapper-cultures-select__surface">
                                  {Number(culture?.surface.toFixed(2))}
                                </span>
                              </div>
                              {culture.irrigation > 0 && (
                                <div className="culture-infos-icon">
                                  <img
                                    src={dropletLogo}
                                    alt={t('alt.irrigationLogo')}
                                    className="dropletLogo"
                                  />
                                  <span>{t('column.mandatoryIrrigation')}</span>
                                </div>
                              )}
                            </div>
                            <Select
                              id="cultureList"
                              value={culture.id}
                              onChange={(e) => handleCultureChange(e, index)}
                            >
                              {culturesList.map((c) => (
                                <MenuItem key={c.id} value={c.id}>
                                  {c.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </Box>
                        </Box>
                      );
                    })}
                  </>
                ) : (
                  <>
                    {culturesWithIndex.map(({ culture, index }, positionInList) => {
                      const seasonFilter = getSeasonFilter(
                        positionInList,
                        culturesWithIndex.length,
                      );
                      const culturesList = getFilteredCultures(
                        seasonFilter,
                        cultures,
                        currentOrganicState,
                      );
                      return (
                        <Box key={index}>
                          <Box>
                            {culture.irrigation > 0 && (
                              <div className="culture-infos-icon">
                                <img
                                  src={dropletLogo}
                                  alt={t('alt.irrigationLogo')}
                                  className="dropletLogo"
                                />
                                <span>{t('column.mandatoryIrrigation')}</span>
                              </div>
                            )}
                            <Select
                              id="cultureList"
                              value={culture.id}
                              onChange={(e) => handleCultureChange(e, index)}
                            >
                              {culturesList.map((c) => (
                                <MenuItem key={c.id} value={c.id}>
                                  {c.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </Box>
                        </Box>
                      );
                    })}
                  </>
                )}
              </React.Fragment>
            </Box>
          </DialogContent>
        </DialogContent>
        <DialogActions>
          <ThemeProvider theme={customButtonTheme}>
            <Button
              type="submit"
              onClick={() => handleUpdate(resultsInfo?.id, currentCulture, year)}
            >
              {t('common.validate')}
            </Button>
          </ThemeProvider>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default ResultsInfo;
